import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import type { CanvasProject, Issue } from '@repo/contracts';
import {
  type ExtensionReportIssuePayload,
  isExtensionBridgeMessage,
} from '@repo/contracts/extension-bridge';

import { trackEvent } from '../analytics/posthog';
import {
  addToFixList,
  canvasKeys,
  createProject,
  listProjects,
} from '../api/canvas-captures';
import { useAuth } from '../auth/use-auth';
import { logger } from '../lib/logger';
import {
  replyReportIssueFailure,
  replyReportIssueSuccess,
} from './extension-captures';
import { classify } from './use-extension-captures';

const log = logger.child({ module: 'extension-report-issue' });

/**
 * The project a report from this page belongs to.
 *
 * Projects are not keyed by URL any more, so there is no lookup by origin to
 * lean on: the report goes to the project whose most recent session was on
 * the same origin, and only when none was does a project named after the
 * host get made for it.
 */
async function projectFor(pageUrl: string): Promise<CanvasProject> {
  const { host, origin } = new URL(pageUrl);
  const projects = await listProjects();
  const match = projects.find((project) => {
    if (!project.lastUrl) return false;
    try {
      return new URL(project.lastUrl).origin === origin;
    } catch {
      return false;
    }
  });
  return match ?? createProject(host);
}

/**
 * Issues reported from the Chrome extension's picker.
 *
 * Mirrors `useExtensionCaptures`'s handoff (the extension holds no
 * credentials, this tab acts with the session it already has) but writes a
 * fix-list entry instead of a board capture: a manually reported issue has
 * no session of its own, so there is nothing to create but the entry: no
 * `CanvasCapture` row and — consistent with every other `skillType`, none of
 * which carry their own image either — no screenshot upload. `projectFor`
 * picks the project and the result is posted straight to `/fix-list`.
 */
export function useExtensionReportIssue(): void {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const handle = useCallback(
    async (requestId: string, issue: ExtensionReportIssuePayload) => {
      const project = await projectFor(issue.pageUrl);

      // Mirrors canvas-feedback.service.ts's persona-issue mapping — a
      // manual pick, like a suggested action, checked nothing and follows no
      // rule, so it carries the same fixed placeholders that mapping
      // established rather than inventing its own.
      const built: Issue = {
        category: 'Manual report',
        categoryNumber: 0,
        description: issue.description,
        elementContext: issue.elementContext,
        id: crypto.randomUUID(),
        // No location of its own, and the reporter's own recommendation
        // rather than a copy of the description — see the same two fields
        // in the standalone extension's `reportIssue` for why.
        locationName: 'Page-level',
        recommendation: issue.recommendation,
        rect: issue.rect,
        severity: issue.severity,
        skillType: 'manual',
      };

      const entry = await addToFixList({
        projectId: project.id,
        issue: built,
        status: 'fix',
      });

      trackEvent('issue_reported', { severity: issue.severity });
      void queryClient.invalidateQueries({ queryKey: canvasKeys.projects() });
      void queryClient.invalidateQueries({ queryKey: canvasKeys.rooms() });

      replyReportIssueSuccess(requestId, {
        projectId: project.id,
        issueId: entry.issue.id,
      });
    },
    [queryClient],
  );

  useEffect(() => {
    const onMessage = (event: MessageEvent<unknown>) => {
      if (event.source !== window) return;
      if (!isExtensionBridgeMessage(event.data)) return;
      if (event.data.kind !== 'report-issue') return;

      const { issue, requestId } = event.data;

      if (!user) {
        trackEvent('issue_report_failed', { reason: 'signed-out' });
        replyReportIssueFailure(requestId, 'signed-out');
        return;
      }

      handle(requestId, issue).catch((err: unknown) => {
        log.error({ err }, 'extension report-issue failed');
        const [failure, message] = classify(err);
        trackEvent('issue_report_failed', { reason: failure });
        replyReportIssueFailure(requestId, failure, message);
      });
    };

    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [handle, user]);
}
